import { Alert, AlertTitle } from "@material-ui/lab";
import { Collection } from "@type-r/models";
import {
  pureRenderProps,
  useIO,
  useLinked,
  useModel,
  useModelCopy
} from "@type-r/react";
import { OrderModel, ShippingNotice } from "api";
import { Loading, NavTree, NavTreeItem, PageLayout, ReadOnly } from "lqx-react";
import * as React from "react";
import { Shipment } from "../../../../../common/shipment";
import { AdditionalInfo } from "./additionalInfo";
import { DialogType, ShowDialog } from "./dialogs";
import { OrderHeader } from "./header";
import { OrderManagement } from "./orderManagement";
import { Shipping } from "./shipping";

export const OrderDetails = ({ orderId, carrierTypes }) => {
  // Local state
  const fetchedOrder = useModel(OrderModel);

  const isReady = useIO(async () => {
    fetchedOrder.id = orderId;
    await fetchedOrder.fetch();
  }, [orderId]);

  // Make a copy of the fetched order, so we can detect what was changed later.
  const order = useModelCopy(fetchedOrder);
  const carrierType = carrierTypes;

  // Opened dialog state
  const $dialog = useLinked<DialogType>(null);
  const readOnly =
    order.isReadOnly || !(order.isCustomerWrite || order.isWriteSupplier);

  return isReady ? (
    <ReadOnly.Provider value={readOnly}>
      <PageLayout
        menu={<Menu shipments={order.shipment} />}
        header={<OrderHeader $dialog={$dialog} order={order} />}
        subheader={-90}
      >
        {order.status === "Unvalidated" && order.unvalidated_reason ? (
          <Alert severity="error">
            <AlertTitle>Unvalidated Reason:</AlertTitle>
            {order.unvalidated_reason}
          </Alert>
        ) : null}
        <OrderManagement order={order} fetched={fetchedOrder} />
        <Shipping
          bill_to={order.bill_to}
          ship_to={order.ship_to}
          end_customer={order.end_customer}
          shipping={order.shipping}
          metadata={order.metadata}
          po_details={order.po_details}
          carrierTypes = {carrierType}
        />

        {order.shipment.length ? (
          <Shipment shipment={order.shipment} />
        ) : (
          undefined
        )}


        <AdditionalInfo
          supplier={order.supplier}
          distributor={order.distributor}
          logs={order.logs}
          uploads={order.uploads}
          conversationId={order.conversation_id}
          $note={order.$.note}
          $comment={order.$.comment}
          $dialog={$dialog}
        />

        <br/>

        <ShowDialog
          $dialog={$dialog}
          order={order}
          fetchedOrder={fetchedOrder}
        />
      </PageLayout>
    </ReadOnly.Provider>
  ) : (
    <Loading />
  );
};

const Menu = pureRenderProps(
  {
    shipments: Collection.of(ShippingNotice),
  },
  ({  shipments }) => (
    <NavTree
      defaultExpanded={[
        "order-management",
        "shipping",
        "shipment",
        "additional-info"
      ]}
    >
      <NavTreeItem label="Order Management" target="order-management">
        <NavTreeItem label="PO Details" target="po-details" />
        <NavTreeItem label="Quote Details" target="quote-details" />
        <NavTreeItem label="Components" target="components" />
      </NavTreeItem>

      <NavTreeItem label="Shipping" target="shipping">
        <NavTreeItem label="Details" target="shipping-details" />
        <NavTreeItem label="Ship To" target="ship-to" />
        <NavTreeItem label="Bill To" target="bill-to" />
        <NavTreeItem label="End Customer" target="end-customer" />
      </NavTreeItem>

      {shipments.length ? (
        <NavTreeItem label="Shipment" target="shipment">
          {shipments.map(x => (
            <NavTreeItem
              key={x.cid}
              label={x.packing_slip_number}
              target={x.cid}
            />
          ))}
        </NavTreeItem>
      ) : (
        undefined
      )}

      <NavTreeItem label="Additional Information" target="additional-info">
        <NavTreeItem label="Supplier" target="supplier" />
        <NavTreeItem label="Customer" target="customer" />
        <NavTreeItem label="Notes" target="notes" />
        <NavTreeItem label="Comments" target="comments" />
        <NavTreeItem label="Documents" target="documents" />
        <NavTreeItem label="All Activity" target="all-activity" />
      </NavTreeItem>
    </NavTree>
  )
);
